import React from 'react';
import ChatListItem from './ChatListItem';
import NewChatDialog from './NewChatDialog';
import ChatListDesign from './design/ChatListDesign';
import EmptyState from './design/EmptyState';
import { useTranslation } from '@/contexts/TranslationContext';

interface Profile {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Conversation {
  id: string;
  name: string | null;
  type: 'direct' | 'group';
  last_message_at: string | null;
  participants: {
    user_id: string;
    profiles: Profile;
  }[];
}

interface ChatListProps {
  conversations: Conversation[];
  selectedConversation: string | null;
  currentUserId: string;
  loading: boolean;
  showNewChat: boolean;
  searchQuery: string;
  users: Profile[];
  onConversationSelect: (conversationId: string) => void;
  onNewChatToggle: () => void;
  onSearchChange: (query: string) => void;
  onUserSelect: (userId: string) => void;
  getConversationName: (conversation: Conversation) => string;
  getConversationAvatar: (conversation: Conversation) => string | null;
}

const ChatList: React.FC<ChatListProps> = ({
  conversations,
  selectedConversation,
  currentUserId,
  loading,
  showNewChat,
  searchQuery,
  users,
  onConversationSelect,
  onNewChatToggle,
  onSearchChange,
  onUserSelect,
  getConversationName,
  getConversationAvatar
}) => {
  const { t } = useTranslation();
  return (
    <ChatListDesign
      title={t('nav.messages')}
      showSearch={false}
      onNewChat={onNewChatToggle}
      loading={loading}
    >
      {showNewChat ? (
        <NewChatDialog
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          users={users}
          onUserSelect={onUserSelect}
        />
      ) : conversations.length > 0 ? (
        <div className="space-y-1">
          {conversations.map((conversation) => (
            <ChatListItem
              key={conversation.id}
              conversation={conversation}
              isSelected={selectedConversation === conversation.id}
              currentUserId={currentUserId}
              onClick={() => onConversationSelect(conversation.id)}
              getConversationName={getConversationName}
              getConversationAvatar={getConversationAvatar}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          type="no-chats"
          onAction={onNewChatToggle}
          actionLabel={t('messages.newChat')}
        />
      )}
    </ChatListDesign>
  );
};

export default ChatList;